import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Layers, Flame, Rocket, BadgeCheck, X, ChevronRight, Sparkles, Check } from 'lucide-react';
import clx from 'classnames';
import styles from './PlanPanel.module.scss';
import { getPlanLabels, getPlanModalMessage } from '../../utils/getActivePlan';
import AvatarIcon from 'Containers/Common/AvatarIcon';
import PrimaryButton from 'Containers/Common/Buttons/PrimaryButton';

const PLAN_ICONS = {
  layers: Layers,
  flame: Flame,
  rocket: Rocket,
};

const PlanPanel = ({
  plan,
  salon,
  compact = false,
  showCloseButton = false,
  onClose,
  onUpgradeClick,
  onManageClick,
}) => {
  const { t } = useTranslation();
  const message = useMemo(() => getPlanModalMessage(plan.id, t), [plan.id, t]);
  const planLabels = useMemo(() => getPlanLabels(plan.id, t), [plan.id, t]);
  const PlanIcon = PLAN_ICONS[plan.icon];
  const MessageIcon = message.type === 'upgrade' ? Sparkles : BadgeCheck;
  const theme = plan.theme || {};
  const isUpgradeModal = message.type === 'upgrade' && !compact;
  const upgradeHighlights = Array.isArray(message.highlights) ? message.highlights : [];

  return (
    <div
      className={clx(styles.plan_panel, {
        [styles.plan_panel_compact]: compact,
        [styles.plan_panel_modal]: !compact,
      })}
      data-plan={plan.id}
      style={{
        '--plan-accent': theme.accent,
        '--plan-accent-soft': theme.accentSoft,
        '--plan-accent-border': theme.accentBorder,
      }}
    >
      <div
        className={clx(styles.header, {
          [styles.header_with_close]: showCloseButton,
        })}
      >
        {showCloseButton ? (
          <button
            type="button"
            className={styles.close_button}
            onClick={onClose}
            aria-label={t('header.plan.close')}
          >
            <X size={18} strokeWidth={2.5} />
          </button>
        ) : null}

        <div className={styles.header_main}>
          <div className={styles.header_avatar}>
            <AvatarIcon
              icon={salon?.icon}
              name={salon?.name}
              size={compact ? '36px' : '44px'}
            />
          </div>
          <div className={styles.header_info}>
            <span className={styles.salon_name}>
              {salon?.name || t('header.plan.salonFallback')}
            </span>
            <div className={styles.plan_row}>
              {PlanIcon ? (
                <PlanIcon
                  className={styles.plan_row_icon}
                  size={14}
                  strokeWidth={2.25}
                  fill={plan.icon === 'flame' ? 'currentColor' : 'none'}
                />
              ) : null}
              <span className={styles.plan_name}>{planLabels.label}</span>
              <span className={styles.plan_status}>{t('header.plan.active')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.body}>
        {isUpgradeModal ? (
          <div className={styles.upgrade_banner}>
            <div className={styles.upgrade_banner_top}>
              <div className={styles.upgrade_icon}>
                <MessageIcon size={20} strokeWidth={2.25} />
              </div>
              <div className={styles.upgrade_copy}>
                {message.eyebrow ? (
                  <span className={styles.upgrade_eyebrow}>{message.eyebrow}</span>
                ) : null}
                <h3 className={styles.upgrade_title}>{message.title}</h3>
                <p className={styles.upgrade_description}>{message.description}</p>
              </div>
            </div>

            {upgradeHighlights.length ? (
              <ul className={styles.upgrade_highlights}>
                {upgradeHighlights.map((item) => (
                  <li key={item}>
                    <Check size={14} strokeWidth={2.75} aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : (
          <div
            className={clx(styles.message_card, {
              [styles.message_card_upgrade]: message.type === 'upgrade',
              [styles.message_card_active]: message.type === 'active',
            })}
          >
            <div className={styles.message_icon}>
              <MessageIcon size={18} strokeWidth={2.5} />
            </div>
            <div className={styles.message_text}>
              <h3>{message.title}</h3>
              <p>{message.description}</p>
            </div>
          </div>
        )}

        {message.type === 'upgrade' ? (
          <div className={styles.upgrade_actions}>
            <PrimaryButton
              text={message.cta}
              background={theme.accent || '#858AF9'}
              width="100%"
              height={compact ? '40px' : '48px'}
              fontSize={compact ? '14px' : '15px'}
              borderRadius="12px"
              onClick={onUpgradeClick}
            />
            {!compact && message.secondaryCta ? (
              <button
                type="button"
                className={styles.upgrade_secondary}
                onClick={onUpgradeClick}
              >
                <span>{message.secondaryCta}</span>
                <ChevronRight size={16} strokeWidth={2.5} aria-hidden="true" />
              </button>
            ) : null}
          </div>
        ) : message.linkCta ? (
          <button
            type="button"
            className={styles.footer_action}
            onClick={onManageClick || onUpgradeClick}
          >
            <span>{message.linkCta}</span>
            <ChevronRight size={16} strokeWidth={2.5} aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default PlanPanel;
